import React, { useState, useEffect, useRef } from "react";
import Specialities from "./Specialities";
import Review from "./Review";
import ScrollToTopButton from "./ScrollToTopButton";
import { Clock, User, CheckCircle, XCircle, Phone, MapPin, Calendar } from "lucide-react";

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("eye");
  const [bgPosition, setBgPosition] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);
  const particlesRef = useRef(null);
  const specialitiesRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);


  // Images array for the sliding background
  const backgroundImages = [
    "images/banner5.jpeg",
    "images/banner1.jpg",
    // "images/banner4.avif",
    // "images/banner2.jpg", 
    // "images/banner3.avif", 
  ];

  const [staffMembers] = useState([
    {
      id: 1,
      name: "Dr. Avinash Utekar, physiotherapist BPT.PGD/RDN",
      image: "/images/phy.jpeg"
    },
    {
      id: 2,
      name: "Pankaj Survase, ",
      image: "/images/nurse1.jpeg"
    },
    {
      id: 3,
      name: "Rohini Adkar, Nurse",
      image: "/images/nurse2.jpeg"
    },
    {
      id: 4,
      name: "Kiran Katkade, Nurse",
      image: "/images/nurse3.jpeg"
    }
  ]);
  

  const scrollToSpecialities = () => {
    specialitiesRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Set loaded state for text animations with a slight delay
    const loadTimer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);
    
    // Sliding background animation
    const interval = setInterval(() => {
      setBgPosition(prev => (prev + 1) % backgroundImages.length);
    }, 5000); // Change image every 5 seconds
    
    // Mouse movement parallax effect
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      
      const { left, top, width, height } = heroRef.current.getBoundingClientRect();
      const x = (e.clientX - left - width / 2) / 25;
      const y = (e.clientY - top - height / 2) / 25;
      
      setMousePosition({ x, y });
    };
    
    // Set canvas size
    const resizeCanvas = () => {
      if (particlesRef.current && heroRef.current) {
        particlesRef.current.width = heroRef.current.offsetWidth;
        particlesRef.current.height = heroRef.current.offsetHeight;
      }
    };

    // Initialize particles animation
    let particles = [];
    const canvas = particlesRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      
      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);
      
      // Create particles
      for (let i = 0; i < 50; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 3 + 1,
          color: `rgba(${Math.floor(Math.random() * 100) + 150}, ${Math.floor(Math.random() * 100) + 150}, 255, ${Math.random() * 0.2 + 0.1})`,
          speedX: Math.random() * 0.5 - 0.25,
          speedY: Math.random() * 0.5 - 0.25
        });
      }
      
      // Animate particles
      const animateParticles = () => {
        if (!canvas) return;
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        particles.forEach(particle => {
          // Move particle
          particle.x += particle.speedX;
          particle.y += particle.speedY;
          
          // Wrap around canvas
          if (particle.x < 0) particle.x = canvas.width;
          if (particle.x > canvas.width) particle.x = 0;
          if (particle.y < 0) particle.y = canvas.height;
          if (particle.y > canvas.height) particle.y = 0;
          
          // Draw particle
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
          ctx.fillStyle = particle.color;
          ctx.fill();
        });
        
        // Connect nearby particles with lines
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < 100) {
              ctx.beginPath();
              ctx.strokeStyle = `rgba(100, 150, 255, ${0.1 * (1 - distance / 100)})`;
              ctx.lineWidth = 0.5;
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.stroke();
            }
          }
        }
        
        requestAnimationFrame(animateParticles);
      };
      
      animateParticles();
    }
    
    document.addEventListener('mousemove', handleMouseMove);
    
    // Cleanup
    return () => {
      clearTimeout(loadTimer);
      clearInterval(interval);
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Toggle between tabs with animation
  const toggleTab = () => {
    setActiveTab(prev => prev === "eye" ? "bone" : "eye");
  };

  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div>
      <div className="relative overflow-hidden h-screen mt-[0px]" ref={heroRef}>
        {/* Added contact numbers in top right corner */}
        <div className="absolute top-2 right-3 sm:top-4 sm:right-6 z-20 text-right">
  <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
    <a 
      href="tel:8788061100" 
      className="text-blue-800 text-sm sm:text-base font-semibold bg-white/80 px-2 sm:px-3 py-1 rounded-lg shadow-md hover:bg-blue-50 transition-colors duration-300 flex items-center truncate"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
      8788061100
    </a>
    <a 
      href="tel:020-24366858" 
      className="text-blue-800 text-sm sm:text-base font-semibold bg-white/80 px-2 sm:px-3 py-1 rounded-lg shadow-md hover:bg-blue-50 transition-colors duration-300 flex items-center truncate"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
      020-24366858
    </a>
    <a 
      href="tel:020-24365287" 
      className="text-blue-800 text-sm sm:text-base font-semibold bg-white/80 px-2 sm:px-3 py-1 rounded-lg shadow-md hover:bg-blue-50 transition-colors duration-300 flex items-center truncate"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
      020-24365287
    </a>
  </div>
</div>
        {/* Animated particles canvas */}
        <canvas 
          ref={particlesRef} 
          className="absolute inset-0 z-0"
          style={{ opacity: 0.7 }}
        ></canvas>
        
        {/* Pattern overlay */}
        <div className="absolute inset-0 bg-white/50 z-0">
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M25 0v15h15v10H25v15H15V25H0V15h15V0h10z' fill='%2322c55e' fill-opacity='0.2'/%3E%3C/svg%3E\")",
              backgroundSize: "30px 30px"
            }}
          ></div>
        </div>
        
        {/* Gradient overlay behind background images */}
        <div 
          className="absolute inset-0 z-[-1] bg-gradient-to-r from-blue-100/10 to-teal-100/10" 
          style={{
            backgroundSize: '200% 200%',
            animation: 'gradient-shift 15s ease infinite',
            transform: `translate(${-mousePosition.x / 2}px, ${-mousePosition.y / 2}px)`,
            transition: 'transform 0.1s ease-out'
          }}
        ></div>

        {/* Background images above gradient */}
        <div className="absolute inset-0 z-0 pointer-events-none">
  {backgroundImages.map((img, index) => (
    <div
      key={index}
      className="absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out bg-cover bg-center will-change-transform"
      style={{
        backgroundImage: `url(${img})`,
        opacity: bgPosition === index ? 0.5 : 0,
        transform:
          bgPosition === index
            ? `scale(1.02) translate(${mousePosition.x * 0.03}px, ${mousePosition.y * 0.03}px)`
            : 'scale(1)',
        transition: 'opacity 1.5s ease-in-out, transform 0.5s ease-out',
      }}
    />
  ))}
</div>


        {/* Animated shapes */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="shape-circle shape-animation-1"></div>
          <div className="shape-square shape-animation-2"></div>
          <div className="shape-triangle shape-animation-3"></div>
        </div>
        
        {/* Content container */}
        <div className="container mx-auto px-4 py-16 md:py-20 lg:py-24 relative z-10 h-full flex items-center">
          {/* Hero Content */}
          <div className="max-w-4xl mx-auto text-center">
            {/* Emergency Services Banner - Added at the top */}
            <div className="emergency-services-banner mb-8">
              <div className="flex items-center justify-center space-x-2">
                <div className="pulse-circle"></div>
                <span className="emergency-text">24 Hours Emergency Services Available</span>
              </div>
            </div>

            <div className="mb-4 inline-block relative">
              <span 
                className={`absolute -left-8 -top-8 text-5xl opacity-20 text-blue-600 font-bold transition-all duration-1000 ${
                  isLoaded ? "rotate-0 opacity-20" : "rotate-45 opacity-0"
                }`}
              >
                +
              </span>
              <span 
                className={`absolute -right-8 -bottom-8 text-5xl opacity-20 text-teal-600 font-bold transition-all duration-1000 ${
                  isLoaded ? "rotate-0 opacity-20" : "-rotate-45 opacity-0"
                }`}
              >
                +
              </span>
            </div>
            
            <h1
              className={`text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 transition-all duration-1000 transform ${
                isLoaded ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
            >
              Unity Ortho and Eye Hospital{" "}
              <span 
                className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent inline-block"
                style={{
                  animation: activeTab === "eye" ? "pulse-text 3s infinite" : "none"
                }}
                onClick={toggleTab}
              >
                {activeTab === "eye" ? "" : "Bone & Vision"}
              </span> 
            </h1>
            
            <p
              className={`text-lg md:text-xl text-gray-600 max-w-3xl mx-auto transition-all duration-1000 delay-200 transform ${
                isLoaded ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
            >
              {activeTab === "eye"
                ? "Expert ophthalmological and orthopedic care with cutting-edge technology and compassionate specialists committed to your health and recovery."
                : "Specialized orthopedic and vision treatments delivered by experienced healthcare professionals using the latest medical innovations."
              }
            </p>
            
            <div>
              {/* CTA Button */}
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                <button className="cta-button secondary" onClick={scrollToSpecialities}>
                  <span className="cta-button-text">Our Specialities</span>
                </button>
              </div>
            </div>
            
            {/* Animated indicator */}
            <div 
              className={`mt-16 animate-bounce transition-all duration-1000 delay-600 transform ${
                isLoaded ? "opacity-100" : "opacity-0"
              }`}
            >
              <svg className="w-6 h-6 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        </div>
      
        {/* CSS for animations */}
        <style jsx>{`
          @keyframes gradient-shift {
            0% { background-position: 0% 50% }
            50% { background-position: 100% 50% }
            100% { background-position: 0% 50% }
          }
          
          @keyframes pulse-text {
            0% { filter: brightness(1); }
            50% { filter: brightness(1.2); }
            100% { filter: brightness(1); }
          }
          
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
            100% { transform: translateY(0px); }
          }
          
          /* Emergency Services Animation */
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.5); opacity: 0.5; }
            100% { transform: scale(1); opacity: 1; }
          }
          
          @keyframes heartbeat {
            0% { transform: scale(1); }
            25% { transform: scale(1.05); }
            40% { transform: scale(1); }
            60% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
          
          @keyframes text-glow {
            0% { text-shadow: 0 0 5px rgba(220, 38, 38, 0.7); }
            50% { text-shadow: 0 0 15px rgba(220, 38, 38, 0.9), 0 0 25px rgba(239, 68, 68, 0.5); }
            100% { text-shadow: 0 0 5px rgba(220, 38, 38, 0.7); }
          }
          
          .emergency-services-banner {
            padding: 10px 20px;
            background: linear-gradient(90deg, rgba(239, 68, 68, 0.05) 0%, rgba(254, 240, 138, 0.1) 100%);
            border-radius: 30px;
            display: inline-flex;
            align-items: center;
            animation: heartbeat 2s infinite ease-in-out;
            border: 1px solid rgba(239, 68, 68, 0.3);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
          }
          
          .pulse-circle {
            width: 12px;
            height: 12px;
            background-color: rgb(220, 38, 38);
            border-radius: 50%;
            animation: pulse 2s infinite;
            margin-right: 8px;
          }
          
          .emergency-text {
            font-weight: 700;
            color: rgb(220, 38, 38);
            font-size: 1.1rem;
            animation: text-glow 2s infinite;
            letter-spacing: 0.5px;
          }
          
          .shape-circle {
            position: absolute;
            width: 300px;
            height: 300px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0) 70%);
            top: 10%;
            right: 5%;
          }
          
          .shape-square {
            position: absolute;
            width: 200px;
            height: 200px;
            background: radial-gradient(circle, rgba(20, 184, 166, 0.1) 0%, rgba(20, 184, 166, 0) 70%);
            bottom: 10%;
            left: 5%;
            transform: rotate(45deg);
          }
          
          .shape-triangle {
            position: absolute;
            width: 0;
            height: 0;
            border-left: 150px solid transparent;
            border-right: 150px solid transparent;
            border-bottom: 260px solid rgba(99, 102, 241, 0.05);
            top: 30%;
            left: 20%;
          }
          
          .shape-animation-1 {
            animation: float 8s ease-in-out infinite;
          }
          
          .shape-animation-2 {
            animation: float 12s ease-in-out infinite;
            animation-delay: -5s;
          }
          
          .shape-animation-3 {
            animation: float 10s ease-in-out infinite;
            animation-delay: -2s;
          }
          
          .cta-button {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 0.75rem 2rem;
            border-radius: 8px;
            font-medium;
            position: relative;
            overflow: hidden;
            transition: all 0.3s ease;
            transform: translateY(0);
          }
          
          .cta-button:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(59, 130, 246, 0.2);
          }
          
          .cta-button:active {
            transform: translateY(-2px);
          }
          
          .cta-button:before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            transition: all 0.6s ease;
          }
          
          .cta-button:hover:before {
            left: 100%;
          }
          
          .primary {
            background: linear-gradient(90deg, #3b82f6, #2563eb);
            color: white;
          }
          
          .secondary {
            background: transparent;
            border: 2px solid #3b82f6;
            color: #3b82f6;
          }
          
          .cta-button-icon {
            transition: all 0.3s ease;
            font-size: 1.2rem;
            line-height: 1;
          }
          
          .primary:hover .cta-button-icon {
            transform: translateX(5px);
          }
          
          .secondary:hover .cta-button-icon {
            transform: rotate(90deg);
          }
        `}</style>
      </div>

      {/* Main Specialities component with the ref */}
      <div ref={specialitiesRef}>
        <Specialities />
      </div>

      <section className="max-w-6xl mx-auto py-12 md:py-20 px-4 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
        {/* Image Side with Animation */}
        <div 
          className={`w-full lg:w-1/2 transform transition-all duration-1000 ease-out ${
            isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
          }`}
        >
          <div className="rounded-xl overflow-hidden shadow-2xl hover:shadow-blue-100 transition-shadow duration-300 relative group">
            <div className="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            <img 
  src="/images/eyecare.jpeg" 
  alt="Eye examination in progress at an ophthalmology clinic" 
  className="w-[800px] h-[600px] object-cover transform group-hover:scale-105 transition-transform duration-700"
/>

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-900/70 to-transparent p-4 md:p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
              <p className="text-white text-sm md:text-base font-medium">Advanced slit lamp examination for comprehensive eye care</p>
            </div>
          </div>
        </div>
        
        {/* Description Side with Animation */}
        <div 
          className={`w-full lg:w-1/2 transform transition-all duration-1000 ease-out delay-300 ${
            isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
          }`}
          >
          <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg border border-gray-100">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 relative">
              <span className="relative inline-block">
                Comprehensive Eye Care
                <span className="absolute -bottom-2 left-0 h-1 w-1/2 bg-blue-600 rounded-full"></span>
              </span>
            </h2>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Our clinic offers advanced eye examination services using state-of-the-art equipment. Our experienced ophthalmologists use slit lamp biomicroscopy to conduct thorough evaluations of your eye health.
            </p>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              Regular eye examinations are crucial for early detection of eye conditions such as glaucoma, cataracts, and retinal disorders. Our team is dedicated to providing personalized care to help maintain your vision health.
            </p>
            
            <div className="mt-6 space-y-4">
              {[
                "Comprehensive Eye Examinations",
                "Advanced Diagnostic Technology",
                "Experienced Eye Care Professionals",
                "Personalized Treatment Plans"
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`flex items-center transform transition-all duration-500 ease-out ${
                    isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
                  }`}
                  style={{ transitionDelay: `${500 + (index * 150)}ms` }}
                >
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 group">
                    <svg className="w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
            
            <div 
              className={`mt-8 transition-all duration-1000 ease-out delay-1000 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
            >
             
            </div>
          </div>
        </div>
        
      </div>
    </section>
     <div className="w-full max-w-6xl mx-auto p-4">
      <div className="flex flex-col lg:flex-row items-center gap-8">
        {/* Doctor Information - Left Side */}
        <div 
          className={`w-full lg:w-1/2 transform transition-all duration-1000 ease-out delay-300 ${
            isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
          }`}
        >
          <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg border border-gray-100">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 relative">
              <span className="relative inline-block">
                Meet Dr. Monika Nimbalkar
                <span className="absolute -bottom-2 left-0 h-1 w-1/2 bg-blue-600 rounded-full"></span>
              </span>
            </h2>
            
            <p className="text-gray-700 mb-4 leading-relaxed">
              Dr. Monika Nimbalkar is a board-certified ophthalmologist with over 15 years of experience in comprehensive eye care. She specializes in advanced diagnostic procedures and cutting-edge treatment options for various eye conditions.
            </p>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              Dr. Monika completed her medical degree M.B.B.S., D.O.M.S., D.N.B (ophthalmologist) and her residency in Ophthalmology at Unity Ortho and Eye Hospital
Eye & Ortho Care

. She is committed to providing the highest quality of care with a personalized approach to each patient's unique needs.
            </p>
            
            <div className="mt-6 space-y-4">
              {[
                "Board Certified Ophthalmologist",
                "15+ Years of Clinical Experience", 
                "Specialist in Retinal Disorders"
              ].map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center transform transition-all duration-500 ease-out ${
                    isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
                  }`}
                  style={{ transitionDelay: `${500 + (index * 150)}ms` }}
                >
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 group">
                    <svg className="w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>
            
            <div
              className={`mt-8 transition-all duration-1000 ease-out delay-1000 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
            >
              <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300 shadow-md hover:shadow-lg">
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>

        {/* Doctor Image - Right Side */}
        <div 
          className={`w-full lg:w-1/2 transform transition-all duration-1000 ease-out delay-500 ${
            isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
          }`}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl transform rotate-3"></div>
            <div className="relative bg-white p-4 rounded-2xl shadow-xl">
              <img 
                  src="/images/drhome.jpeg"  
                alt="Dr. Monika Nimbalkar- Ophthalmologist" 
                className="w-full h-96 md:h-full object-cover rounded-xl"
              />
             
            </div>
          </div>
        </div>
      </div>
    </div>
      <section>
  <div className="max-w-7xl mx-auto my-12 px-4">
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-b from-sky-700 to-sky-900 px-6 py-4">
        <h2 className="text-xl md:text-2xl font-bold text-white text-center">Hospital Consultation Hours</h2>
      </div>
      
      {/* Content container - full width */}
      <div className="p-6">
        {/* Two-column layout container */}
        <div className="flex flex-col md:flex-row">
          {/* Left column: Summary information - takes more space */}
          <div className="w-full md:w-2/3 order-2 md:order-1 md:pr-4">
            <div className="p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl shadow-md h-full">
              <h2 className="text-center text-2xl font-bold text-blue-800 mb-6">Unity Ortho And Eye Hospital</h2>
              
              {/* Doctor Cards */}
              <div className="grid grid-cols-1 gap-y-8">
  {/* Orthopedic Doctor Card */}
  <div className="bg-white p-3 rounded-lg shadow-lg mb-20 mt-10 border-l-4 border-blue-500 hover:shadow-xl transition-shadow duration-300">
    <div className="flex items-start">
      <div className="bg-blue-100 p-3 rounded-full mr-4">
        <User className="h-6 w-6 text-blue-700" />
      </div>
      <div>
        <h3 className="font-bold text-blue-800 text-lg">Dr. Jagjit M. Naik-Nimbalkar</h3>
        <p className="text-gray-700 font-medium mt-1">Orthopedic Specialist</p>
        <div className="flex items-center mt-3 text-blue-600">
          <Clock className="h-4 w-4 mr-2" />
          <p className="text-sm">Morning & Evening Consultations</p>
        </div>
      </div>
    </div>
  </div>

  {/* Eye Specialist Card */}
  <div className="bg-white p-5 rounded-lg shadow-lg border-l-4 border-indigo-500 hover:shadow-xl transition-shadow duration-300">
    <div className="flex items-start">
      <div className="bg-indigo-100 p-3 rounded-full mr-4">
        <User className="h-6 w-6 text-indigo-700" />
      </div>
      <div>
        <h3 className="font-bold text-indigo-800 text-lg">Dr. Monika J. Naik-Nimbalkar</h3>
        <p className="text-gray-700 font-medium mt-1">Eye Specialist</p>
        <div className="flex items-center mt-3 text-indigo-600">
          <Clock className="h-4 w-4 mr-2" />
          <p className="text-sm">Morning & Evening Consultations</p>
        </div>
      </div>
    </div>
  </div>
</div>

      
            </div>
          </div>
          
          {/* Right column: Image showcase - takes less space */}
          <div className="w-full md:w-1/3 order-1 md:order-2 flex justify-center md:justify-end items-center mb-6 md:mb-0">
            <div 
              className="relative w-full max-w-xs md:max-w-full transition-all duration-300"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              style={{
                transform: isHovered ? 'translateY(-5px)' : 'translateY(0)',
                boxShadow: isHovered ? '0 20px 25px -5px rgba(0, 0, 0, 0.2)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}
            >
              <img 
                src="images/unityhospital.png" 
                alt="Naik-Nimbalkar Hospital Consultation Hours" 
                className="w-full rounded-lg"
              />
              {/* Decorative elements */}
              <div className="absolute -top-6 -left-8 w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-blue-700 text-center mb-12">Meet Our Medical Team</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {staffMembers.map((member) => (
          <div key={member.id} className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:transform hover:-translate-y-2">
            <div className="h-64 w-full">
              <img 
                src={member.image} 
                alt={member.name} 
                className="w-full h-full object-cover" 
              />
            </div>
            <div className="p-4 text-center">
              <h2 className="text-xl font-semibold text-blue-700">{member.name}</h2>
            </div>
          </div>
        ))}
      </div>
    </div>
      
      <Review />
      <ScrollToTopButton />
    </div>
  );
};

export default Hero;